import type { CSSProperties, ReactNode } from 'react';
import { isValidElement } from 'react';
import { CircleCheckIcon, Loader2Icon } from 'lucide-react';
import { type ExternalToast, toast as sonnerToast, Toaster as Sonner, type ToasterProps } from 'sonner';

import { SeverityIcon } from '@/modus/Severity';
import { isPlainRecord } from '@/utils/object';
import { cn } from '@/utils/styles';

export const DEFAULT_TOAST_LIFETIME = 4_000;

const sonnerError = sonnerToast.error;
const sonnerPromise = sonnerToast.promise;

type PromiseErrorOption = NonNullable<Parameters<typeof sonnerPromise>[1]>['error'];
type ExtendedErrorResult = ExternalToast & { message: ReactNode };

/**
 * Wraps a `toast.promise` `error` option so, whatever shape it resolves to
 * (message, extended result, or a function returning either), the final
 * error toast gets the same "stays until dismissed" defaults as `toast.error`.
 */
const withErrorToastDefaults = (
  error: PromiseErrorOption,
): ((arg: unknown) => Promise<ExtendedErrorResult>) | undefined => {
  if (error === undefined) return undefined;
  return async (arg: unknown) => {
    const resolved = typeof error === 'function' ? await error(arg) : error;
    const isExtendedResult = isPlainRecord(resolved) && !isValidElement(resolved);
    const base: ExtendedErrorResult = isExtendedResult
      ? (resolved as ExtendedErrorResult)
      : { message: resolved as ReactNode };
    return { duration: Infinity, closeButton: true, ...base };
  };
};

export const toast: typeof sonnerToast = Object.assign(
  (...args: Parameters<typeof sonnerToast>) => sonnerToast(...args),
  sonnerToast,
  {
    /**
     * Error toasts stay on screen until dismissed (instead of auto-dismissing
     * like other toast types) since errors are more important not to miss;
     * a close button lets the user dismiss them explicitly.
     */
    error: (message: Parameters<typeof sonnerError>[0], data?: ExternalToast) =>
      sonnerError(message, { duration: Infinity, closeButton: true, ...data }),
    promise: <ToastData,>(
      promise: Parameters<typeof sonnerPromise<ToastData>>[0],
      data?: Parameters<typeof sonnerPromise<ToastData>>[1],
    ) =>
      sonnerPromise(promise, {
        ...data,
        error: withErrorToastDefaults(data?.error),
      }),
  },
);

// TODO: limit list of props (we have way too many from ToasterProps)
export const Toast = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      theme="dark"
      {...props}
      className={cn('toaster group', props.className)}
      position="bottom-center"
      duration={DEFAULT_TOAST_LIFETIME}
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <SeverityIcon level="info" tone="current" />,
        warning: <SeverityIcon level="warning" tone="current" />,
        error: <SeverityIcon level="critical" tone="current" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          '--normal-bg': '#2C2C2C',
          '--normal-text': '#FFFFFF',
          '--normal-border': '#414141',
          '--border-radius': '16px',
          // Do not remove: Radix Dialog sets body `pointer-events: none` while open, and the
          // toaster inherits it, so without this the close button is visible and dead.
          pointerEvents: 'auto',
        } as CSSProperties
      }
    />
  );
};

Toast.displayName = 'Toast';
